import { User } from '@auth0/auth0-react'

import { UserDTO } from '../../models/UserDTO.model'
import ScrubstrApiQueryService from '../ScrubstrApiQueryService/ScrubstrApiQueryService'

// localStorage key prefix for caching profile fields that are not yet in the JWT
// (until the Auth0 Post-Login Action propagates them as custom claims)
const PROFILE_CACHE_PREFIX = 'scrubstr_profile_cache_'

type ProfileCache = {
  position_title?: string
  nursing_specialization?: string
  work_experience?: string
  work_settings?: string
}

const readProfileCache = (userId: string): ProfileCache => {
  try {
    const raw = localStorage.getItem(`${PROFILE_CACHE_PREFIX}${userId}`)
    return raw ? (JSON.parse(raw) as ProfileCache) : {}
  } catch {
    return {}
  }
}

const writeProfileCache = (userId: string, data: ProfileCache): void => {
  try {
    // Merge with existing cache so partial updates don't wipe other fields
    const existing = readProfileCache(userId)
    localStorage.setItem(`${PROFILE_CACHE_PREFIX}${userId}`, JSON.stringify({ ...existing, ...data }))
  } catch {
    // ignore write failures (e.g. private browsing quota)
  }
}

export default class UserService {
  /**
   * @description Updates the user
   *
   * @param id
   * @param data
   * @param token
   *
   * @returns Promise<void>
   */
  public static async updateUser(
    id: string,
    data: Partial<
      Record<'name' | 'position_title' | 'nursing_specialization' | 'work_experience' | 'work_settings', string>
    >,
  ): Promise<UserDTO> {
    const scrubstrApiQueryService = new ScrubstrApiQueryService()
    const result = await scrubstrApiQueryService.patch<UserDTO>({
      data,
      endpoint: `user/${encodeURIComponent(id)}`,
    })
    // Cache all profile fields locally so formatUser can return them immediately
    // after save — before the Auth0 JWT is refreshed with the new claims.
    writeProfileCache(id, {
      nursing_specialization: result.nursing_specialization,
      position_title: result.position_title,
      work_experience: result.work_experience,
      work_settings: result.work_settings,
    })
    // Notify any mounted component (TopNav, UserAvatar, etc.) to re-read the cache.
    window.dispatchEvent(new CustomEvent('scrubstr_profile_updated'))
    return result
  }

  /**
   * @description Removes the user from the system and deletes all their data
   *
   * @param id
   * @param token
   *
   * @returns Promise<void>
   */
  public static async deleteUser(id: string): Promise<void> {
    const scrubstrApiQueryService = new ScrubstrApiQueryService()
    await scrubstrApiQueryService.delete<boolean>({
      endpoint: `user/${encodeURIComponent(id)}`,
    })
  }

  public static async resendVerificationEmail(): Promise<void> {
    const scrubstrApiQueryService = new ScrubstrApiQueryService()
    await scrubstrApiQueryService.post<{ success: boolean }>({
      data: {},
      endpoint: 'user/verification-email',
    })
  }

  public static async checkVerificationStatus(): Promise<boolean> {
    const scrubstrApiQueryService = new ScrubstrApiQueryService()
    const result = await scrubstrApiQueryService.get<{ email_verified: boolean }>({
      endpoint: 'user/me/email-verification',
    })
    return result.email_verified
  }

  /**
   * @description Takes in a user object fetched from the Auth0 hook and formats it to match the UserDTO model
   *
   * @param user
   *
   * @returns UserDTO
   */
  public static formatUser(user: User): UserDTO {
    const sub = user.sub || ''
    const cache = readProfileCache(sub)
    return {
      email: user.email || '',
      id: sub,
      isSocial: !!user['https://app.scrubstr.com/idp']?.find((idp: { isSocial: boolean }) => idp.isSocial),
      name: user.name || '',
      // Fall back to localStorage cache while the Auth0 JWT hasn't been refreshed
      // yet (e.g. right after onboarding save, before loginWithRedirect fires).
      nursing_specialization:
        user['https://app.scrubstr.com/nursing_specialization'] || cache.nursing_specialization || '',
      permissions: Array.isArray(user['https://app.scrubstr.com/permissions'])
        ? user['https://app.scrubstr.com/permissions']
        : [],
      picture: user.picture || '',
      position_title: user['https://app.scrubstr.com/position_title'] || cache.position_title || '',
      work_experience: user['https://app.scrubstr.com/work_experience'] || cache.work_experience || '',
      work_settings: user['https://app.scrubstr.com/work_settings'] || cache.work_settings || '',
    }
  }
}
